import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { SilentError } from 'init/silent-error';
import { z } from 'zod/v4';

import { AttachedInvoicesSchema } from './schemas/attached-invoices';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from './utils';

const V2AssigneeSchema = z.object({
  id: z.coerce.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  profilePicture: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
});

export type V2Assignee = z.infer<typeof V2AssigneeSchema>;

const V2TaskTypeSchema = z.object({
  id: z.coerce.string(),
  uid: z.string(),
  name: z.string(),
  isDeletable: z.boolean().optional(),
  defaultTitle: z.string().nullable().optional(),
  defaultDescription: z.string().nullable().optional(),
  defaultDueOffsetDays: z.number().nullable().optional(),
});

const V2PhoneSchema = z.object({
  id: z.coerce.string(),
  number: z.string(),
});

const V2DebtorSchema = z.object({
  id: z.coerce.string(),
  fullName: z.string().optional(),
  emails: z.array(z.string()),
  phones: z.array(V2PhoneSchema),
});

const V2DebtorReactionSchema = z.object({
  id: z.coerce.string(),
  comment: z.string(),
  authorName: z.string().nullable(),
  createdAt: z.string().transform(dayjs),
  invoiceId: z.coerce.string(),
  invoiceReference: z.string(),
});

export const V2TaskShowSchema = z.object({
  id: z.coerce.string(),
  title: z.string(),
  description: z.string().nullable(),
  mandatory: z.boolean(),
  skipped: z.boolean(),
  completed: z.boolean(),
  userId: z.coerce.number(),
  createdAt: z.string().transform(dayjs),
  dueDate: z.string().transform(dayjs).nullable(),
  daysPostponed: z.number(),
  taskTypeId: z.coerce.number(),
  startOffsetDays: z.number().nullable(),
  statusKey: z.enum(['to_do', 'pending', 'done', 'skipped'] as const),
  status: z.string(),
  aiCallAutogenerated: z.boolean(),
  debtorReaction: V2DebtorReactionSchema.nullable(),
  assignee: V2AssigneeSchema,
  additionalAssignees: z.array(V2AssigneeSchema),
  taskType: V2TaskTypeSchema,
  debtor: V2DebtorSchema.nullable(),
  invoices: AttachedInvoicesSchema.nullable().optional(),
});

export type V2Task = z.infer<typeof V2TaskShowSchema>;

export async function loadTaskQueryFn(axiosInstance: Promise<AxiosInstance>, id: string) {
  const instance = await axiosInstance;
  const { data } = await instance.get(`/tasks/${id}`);
  const parseResult = V2TaskShowSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'task');
    throw new SilentError();
  }
  return parseResult.data;
}

export function useLoadTask(id: string | undefined, options: QueryOptions<V2Task> = {}) {
  const axiosInstance = useAxiosInstance();
  const queryResult = useQuery<V2Task, ApiError>({
    queryKey: ['task', id],
    queryFn: () => loadTaskQueryFn(axiosInstance, id!),
    enabled: id !== undefined,
    ...options,
  });
  return addResourceNameToQueryResult<V2Task, ApiError, 'task'>('task', queryResult);
}
